import { Box, Button, Card, Divider, InputAdornment, Stack, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import Grid from '@mui/material/Grid2';

import ContinueAndPreviewContainer from 'components/v2/container/ContinueAndPreviewContainer';
import { LAYOUT_SPACING } from 'constants/ui/style';
import { useLocalization } from 'localization';
import { ArrowRightIcon } from 'components/v2/icons/ArrowRightIcon';
import { InputField } from 'components/v2/input/InputField';
import { colors } from 'theme/mui/colors';
import ImageIcon from 'components/v2/icons/ImageIcon';
import { useCreateAdsCampaignOrderStep3 } from 'hooks/services/useCreateAdsCampaignOrderStep3';
import FeaturedImage from 'components/v2/shared/FeaturedImage';
import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import { TrashIcon } from 'components/v2/icons/TrashIcon';
import AdsPreview from 'components/v2/shared/AdsPreview';
import MediaViewModal from 'components/v2/media/MediaViewModal';

const CreateAdsCampaignOrderStep3 = () => {
  const { t } = useLocalization();

  const { control } = useFormContext();
  const { apiStates, clientStates, actions } = useCreateAdsCampaignOrderStep3();
  const { data, isLoading } = apiStates;

  return (
    <Stack direction="row" justifyContent="space-between" sx={{ height: '100%' }}>
      <Stack
        spacing={LAYOUT_SPACING.FORM.md}
        sx={{ width: '100%', padding: '1rem 1rem 1rem 0', overflowY: 'auto', marginBottom: '2rem' }}
      >
        <Stack spacing="1.5rem">
          <Typography variant="h3">Build display ad</Typography>
          <Typography variant="body1">
            Design compelling ads to showcase your real estate listings. Add an eye-catching title, highlight the
            property’s key features, and include professional images to attract buyers or renters
          </Typography>
        </Stack>
        <Stack
          sx={{
            mt: '2.5rem !important',
            mb: '2.5rem !important',
          }}
          gap="1.25rem"
        >
          <InputField control={control} label="Title" name="title" />
          <InputField control={control} label="Open house message" name="message" />
          <InputField
            control={control}
            label="Property price"
            name="price"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: -1.75 }}>
                    <Box sx={{ px: '1rem', py: '1rem', bgcolor: colors.gray[100] }}>DKK</Box>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-input': { pl: 0.75 },
            }}
          />
        </Stack>
        <Divider variant="middle" />
        <Stack
          sx={{
            mt: '2.5rem !important',
          }}
          gap="1.25rem"
        >
          <Stack spacing="1.5rem">
            <Typography variant="h3">Choose images</Typography>
            <Typography variant="body1">
              Choose up to 4 images you want to feature on your display banners. You can pick between all the images
              connected to the case.
            </Typography>
            <Card>
              {clientStates.materials.length ? (
                <>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <ImageIcon />
                    <Typography variant="h5">Featured images</Typography>
                  </Stack>
                  <Grid container spacing={1.25}>
                    {clientStates.materials.map(img => (
                      <Grid key={img.id} size={{ xs: 12, sm: 6 }}>
                        <Box position="relative">
                          <FeaturedImage
                            onClick={() => actions.handleShowMaterial(img.id)}
                            fileName={img.fileName ?? ''}
                            thumbnailUrl={img.source}
                            displayTooltip
                            imgStyles={{ width: '100%', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', cursor: 'pointer' }}
                          />
                          <Stack
                            onClick={() => actions.handleRemoveImage(img.id)}
                            direction="row"
                            gap={2}
                            position="absolute"
                            top="0.5rem"
                            right="0.5rem"
                          >
                            <TrashIcon
                              color="#EF4444"
                              style={{
                                height: '100%',
                                width: '100%',
                                borderRadius: '8px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                backgroundColor: colors.base.white,
                              }}
                            />
                          </Stack>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                  <Typography variant="h5">{t('common.terms.noImages')}</Typography>
                </Stack>
              )}
            </Card>
            <SkeletonContainer loading={isLoading} rows={{ xs: 1 }} columns={{ xs: 1 }}>
              <Grid container spacing={1.25}>
                {data?.materials?.length
                  ? data.materials
                      .filter(img => img.id)
                      .map(img => (
                        <Grid key={img.id} size={{ xs: 4, sm: 3, md: 2 }}>
                          <FeaturedImage
                            onClick={() => actions.handleChooseImages(img)}
                            fileName={img.fileName ?? ''}
                            thumbnailUrl={img.source}
                            imgStyles={{
                              width: '100%',
                              height: '7.5rem',
                              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                              cursor: 'pointer',
                            }}
                            displayTooltip={false}
                          />
                        </Grid>
                      ))
                  : null}
              </Grid>
            </SkeletonContainer>
          </Stack>
        </Stack>
      </Stack>
      {clientStates.isOpenAsset && (
        <MediaViewModal
          onClose={actions.closeViewMaterial}
          isOpen={clientStates.isOpenAsset}
          mediaItems={clientStates.transformMediaProperties ?? []}
          activeIndex={clientStates.currentOpeningFile}
        />
      )}

      <ContinueAndPreviewContainer
        continueElement={
          <Stack gap={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <Typography variant="h4">{t('common.terms.totalPrice')}</Typography>
              <Typography variant="h4">{1400} DKK</Typography>
            </Stack>
            <Button
              size="large"
              variant="contained"
              fullWidth
              endIcon={<ArrowRightIcon />}
              onClick={() => actions.handleNextStep()}
              sx={{ minWidth: 'fit-content' }}
            >
              {t('common.terms.continue')}
            </Button>
          </Stack>
        }
        previewElement={<AdsPreview loading={isLoading} src={data?.materials?.at(-1)?.source ?? ''} />}
      />
    </Stack>
  );
};

export default CreateAdsCampaignOrderStep3;
